import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/use-language";
import { DemoButton, PageHero } from "@/components/sections/PageHero";
import { Card, Chip, Em, Section, SectionHeading } from "@/components/sections/Section";
import { ArrowLink, CustomerVoices, IsaeSection, NumberedCards, SwitchSystemCta } from "@/components/sections/blocks";
import tgaLogo from "@/assets/tga-logo.webp";
import tleLogo from "@/assets/tle-logo.webp";
import officePhoto from "@/assets/photos/tiego-kontoret.webp";
import lkabLogo from "@/assets/logos/customers/lkab.webp";
import scandiStandardLogo from "@/assets/logos/customers/scandi-standard.webp";
import sodraLogo from "@/assets/logos/customers/sodra.webp";
import quinyxLogo from "@/assets/logos/customers/quinyx.webp";
import novedoLogo from "@/assets/logos/customers/novedo.webp";
import brovikenLogo from "@/assets/logos/customers/broviken.webp";

const customerLogos = [
  { name: "LKAB", logo: lkabLogo },
  { name: "Scandi Standard", logo: scandiStandardLogo },
  { name: "Södra", logo: sodraLogo },
  { name: "Quinyx", logo: quinyxLogo },
  { name: "Novedo", logo: novedoLogo },
  { name: "Broviken", logo: brovikenLogo },
];

/** Startsidan (TW-62). Sektionerna ligger i src/components/sections/. */
const Index = () => {
  const { t, localePath } = useLanguage();

  const systems = [
    {
      key: "tga",
      name: "Tiego Group Accounting",
      logo: tgaLogo,
      title: t("TGA – Varje siffra går att följa, förstå och förklara.", "TGA – Every figure can be traced, understood and explained."),
      body: t(
        "Ca 50 koncerner konsoliderar i TGA, många av dem serieförvärvare och riskkapitalbolag med strukturer som ändras mitt i året.",
        "Around 50 groups consolidate in TGA, many of them serial acquirers and private equity-owned companies with structures that change mid-year.",
      ),
      standards: ["K3", "IFRS", "RKR"],
      link: t("Läs mer om TGA", "Read more about TGA"),
      href: localePath("/tga"),
      accent: "border-t-gold",
    },
    {
      key: "tle",
      name: "Tiego Lease Engine",
      logo: tleLogo,
      title: t("TLE – Rätt från början. Spårbart hela vägen.", "TLE – Right from the start. Traceable all the way."),
      body: t(
        "Över 30 000 leasingkontrakt räknas i TLE varje månad, för ett hundratal koncerner med några av Sveriges största fordonsflottor och fastighetsportföljer.",
        "Over 30,000 lease contracts are calculated in TLE every month, for around a hundred groups with some of Sweden's largest vehicle fleets and property portfolios.",
      ),
      standards: ["IFRS 16", "K3", "RKR R5"],
      link: t("Läs mer om TLE", "Read more about TLE"),
      href: localePath("/tle"),
      accent: "border-t-teal",
    },
  ];

  const audiences = [
    {
      title: t("Börsbolag", "Listed companies"),
      body: t(
        "Koncernredovisning enligt IFRS där varje eliminering kan följas ner till transaktionen.",
        "IFRS group accounting where every elimination can be traced down to the transaction.",
      ),
    },
    {
      title: t("Riskkapitalbolag och PE-ägda grupper", "Private equity-owned groups"),
      body: t(
        "Minoriteter, kapitalandelar och underkoncerner som ändras mitt i året.",
        "Minority interests, equity holdings and subgroups that change mid-year.",
      ),
    },
    {
      title: t("Serieförvärvare", "Serial acquirers"),
      body: t(
        "Hela räkenskapsåret läses in vid förvärv, med proforma och legalt utfall isär från förvärvsdagen.",
        "The full financial year is loaded on acquisition, with pro forma and legal results kept apart from the acquisition date.",
      ),
    },
    {
      title: t("Kommuner och regioner", "Municipalities and regions"),
      body: t(
        "Leasing enligt RKR R5 och kommunkoncernen med RKR och K3 parallellt.",
        "Leasing under RKR R5, and the municipal group with RKR and K3 in parallel.",
      ),
      link: { to: localePath("/kommuner"), label: t("Läs om kommuner", "About municipalities") },
    },
  ];

  const reasons = [
    {
      title: t("Byggt av koncernredovisare", "Built by group accountants"),
      body: t(
        "Systemen är utvecklade av ekonomer och utvecklare tillsammans, för hur bokslutet faktiskt går till.",
        "The systems are developed by accountants and developers together, for how the close actually works.",
      ),
    },
    {
      title: t("Konsulter bakom systemet", "Consultants behind the system"),
      body: t(
        "Den som svarar kan både systemet och redovisningsfrågan bakom. Vi kan driva ert projekt eller vägleda er att göra det mesta själva.",
        "Whoever answers knows both the system and the accounting question behind it. We can run your project or guide you to do most of it yourselves.",
      ),
    },
    {
      title: t("Data in utan filhantering", "Data in without file handling"),
      body: t(
        "15 färdiga integrationer till ekonomisystem som Fortnox, Xero, Dynamics 365, NetSuite, Visma Net, Jeeves och Monitor. Leasingavtal läses in från leasegivarna, och avtal i PDF tolkas med AI.",
        "15 ready-made integrations with ERP systems such as Fortnox, Xero, Dynamics 365, NetSuite, Visma Net, Jeeves and Monitor. Lease agreements are imported from the lessors, and PDF contracts are interpreted with AI.",
      ),
    },
    {
      title: t("Säkerhet som granskas", "Security that is audited"),
      body: t(
        "ISAE 3402 Type 1 genomförd 2025 och Type 2-granskning pågår 2026.",
        "ISAE 3402 Type 1 completed in 2025 and Type 2 audit under way in 2026.",
      ),
    },
  ];

  const services = [
    {
      title: "Outsourcing",
      body: t(
        "När ni behöver avlastning tar vi hand om löpande koncernredovisning eller leasingbokföring, vid toppar, vakanser eller som en permanent lösning. Arbetet görs i samma system och med samma rutiner, så kontinuiteten finns kvar.",
        "When you need relief, we take care of ongoing group accounting or lease bookkeeping – during peaks, vacancies or as a permanent solution. The work is done in the same system and with the same routines, so continuity is maintained.",
      ),
    },
    {
      title: "Education",
      body: t(
        "En prenumeration med dokumentation, filmer och erfarenhetsartiklar för er som arbetar i TGA och TLE.",
        "A subscription with documentation, videos and experience articles for those who work in TGA and TLE.",
      ),
    },
    {
      title: t("Krönikor, filmer och nyheter", "Columns, videos and news"),
      body: t(
        "Expertkunskap om IFRS, K3 och RKR, och steg-för-steg-guider i systemen.",
        "Expert insight on IFRS, K3 and RKR, and step-by-step guides to the systems.",
      ),
      links: [
        { to: localePath("/nyheter"), label: t("Krönikor och nyheter", "Columns and news") },
        { to: localePath("/filmer"), label: t("Filmer", "Videos") },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <PageHero
          badge={t("Utvecklat av redovisningsspecialister sedan 2012", "Developed by accounting specialists since 2012")}
          title={
            <>
              {t("System för koncernredovisning", "Software for group accounting")}
              <br className="hidden sm:block" /> {t("och leasingredovisning", "and lease accounting")}
            </>
          }
          lead={t(
            "Sammanställ koncernens redovisning i TGA eller hantera leasingavtal och leasingredovisning i TLE. Våra redovisningsspecialister hjälper dig med införandet och frågor längs vägen.",
            "Consolidate your group's accounts in TGA, or manage lease agreements and lease accounting in TLE. Our accounting specialists help you with the implementation and with questions along the way.",
          )}
          actions={
            <>
              <DemoButton location="start-hero" />
              <Button asChild variant="heroOutline" size="xl">
                <Link to={localePath("/tga")}>{t("Utforska koncernredovisning", "Explore group accounting")}</Link>
              </Button>
              <Button asChild variant="heroOutline" size="xl">
                <Link to={localePath("/tle")}>{t("Utforska leasingredovisning", "Explore lease accounting")}</Link>
              </Button>
            </>
          }
          stats={[
            { value: "110", plus: true, label: t("Kunder", "Customers") },
            { value: t("30 000", "30,000"), plus: true, label: t("Leasingkontrakt varje månad", "Lease contracts every month") },
            { value: t("3 700", "3,700"), label: t("Rapporterande enheter", "Reporting entities") },
            { value: "2012", label: t("Sedan", "Since") },
          ]}
        />

        {/* Våra system */}
        <Section>
          <SectionHeading eyebrow={t("Våra system", "Our systems")} title={t("Två system, en grundlighet", "Two systems, the same thoroughness")} />
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {systems.map((s) => (
              <article key={s.key} className={`flex flex-col rounded-2xl border-t-4 bg-navy-deep p-8 text-white shadow-card lg:p-10 ${s.accent}`}>
                <div className="mb-6 flex items-center gap-3">
                  <img src={s.logo} alt="" width={200} height={169} loading="lazy" decoding="async" className="h-8 w-8 object-contain" />
                  <span className="text-sm font-semibold uppercase tracking-widest text-white/60">{s.name}</span>
                </div>
                <h3 className="mb-4 font-display text-2xl font-normal leading-snug lg:text-3xl">{s.title}</h3>
                <p className="mb-6 flex-1 leading-relaxed text-white/65">{s.body}</p>
                <div className="mb-8 flex flex-wrap gap-2">
                  {s.standards.map((x) => (
                    <Chip key={x} dark>
                      {x}
                    </Chip>
                  ))}
                </div>
                <ArrowLink to={s.href} dark>
                  {s.link}
                </ArrowLink>
              </article>
            ))}
          </div>
        </Section>

        {/* Våra kunder */}
        <Section tone="muted">
          <SectionHeading eyebrow={t("Våra kunder", "Our customers")} title={t("Från börsbolag till kommuner", "From listed companies to municipalities")} />
          <div className="mt-12 grid grid-cols-2 items-center gap-8 sm:grid-cols-3 lg:grid-cols-6">
            {customerLogos.map((c) => (
              <div key={c.name} className="flex h-14 items-center justify-center">
                <img
                  src={c.logo}
                  alt={c.name}
                  width={160}
                  height={56}
                  loading="lazy"
                  decoding="async"
                  className="max-h-full max-w-full object-contain grayscale transition-all duration-300 hover:grayscale-0"
                />
              </div>
            ))}
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {audiences.map((a) => (
              <Card key={a.title} className="flex flex-col">
                <h3 className="mb-3 font-display text-lg font-medium text-foreground">{a.title}</h3>
                <p className="flex-1 leading-relaxed text-muted-foreground">{a.body}</p>
                {a.link && (
                  <div className="mt-5">
                    <ArrowLink to={a.link.to}>{a.link.label}</ArrowLink>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </Section>

        {/* Varför Tiego */}
        <Section>
          <SectionHeading eyebrow={t("Det som särskiljer oss", "What sets us apart")} title={t("Varför Tiego", "Why Tiego")} className="mb-12" />
          <NumberedCards items={reasons} />
        </Section>

        <IsaeSection variant="full" tone="muted" />

        <CustomerVoices title={t("Vad våra kunder säger", "What our customers say")} quoteIds={["eidra", "crown-energy-analys"]} />

        <SwitchSystemCta
          tone="muted"
          location="start-byta-system"
          title={t("Berätta om er koncern. Vi visar hur ett byte går till.", "Tell us about your group. We'll show you how a switch works.")}
          paragraphs={[
            t(
              "Vi vill förstå vad som varit ineffektivt i er nuvarande lösning och vart ni vill. Med den bilden visar vi på 1–2 timmar hur vi gör och hur andra koncerner har tagit sig igenom bytet. Kostnadsfritt och under sekretess.",
              "We want to understand what has been inefficient in your current solution and where you want to go. With that picture, we show you in 1–2 hours how we work and how other groups have made the switch. Free of charge and under confidentiality.",
            ),
          ]}
        />

        {/* IFRS-luncher */}
        <Section tone="dark">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-5 py-2.5">
                <span className="h-2 w-2 animate-pulse rounded-full bg-gold" />
                <span className="text-sm font-medium text-white/80">{t("IFRS-luncher", "IFRS lunches")}</span>
              </div>
              <h2 className="font-display text-3xl font-normal leading-tight text-white md:text-4xl lg:text-5xl">
                {t("Du läser IASB-nyheterna. Men vem ", "You read the IASB news. But who do you ")}
                <Em dark>{t("diskuterar", "discuss")}</Em>
                {t(" du dem med?", " them with?")}
              </h2>
            </div>
            <div className="self-center">
              <p className="mb-8 text-lg leading-relaxed text-white/65">
                {t(
                  "Tiego bjuder in till ett mindre nätverk med återkommande IFRS-luncher för dig som arbetar kvalificerat med IFRS och koncernrapportering. Varje lunch utgår från ett urval från IASB, IFRIC och EFRAG, följt av ett öppet samtal om vad det betyder för redovisning, data och rapporter. Ett forum för erfarenhetsutbyte, inte ett säljmöte.",
                  "Tiego hosts a small network with recurring IFRS lunches for those who work at an advanced level with IFRS and group reporting. Each lunch starts from a selection of news from the IASB, IFRIC and EFRAG, followed by an open conversation about what it means for accounting, data and reports. A forum for exchanging experience, not a sales meeting.",
                )}
              </p>
              <Button asChild variant="hero" size="lg" className="h-12">
                <Link to={localePath("/nyheter/ifrs-lunch")}>{t("Anmäl intresse", "Register interest")}</Link>
              </Button>
            </div>
          </div>
        </Section>

        {/* Tjänster */}
        <Section>
          <SectionHeading eyebrow={t("Tjänster", "Services")} title={t("Tjänster utöver systemen", "Services beyond the systems")} className="mb-12" />
          <div className="grid gap-6 md:grid-cols-3">
            {services.map((s) => (
              <Card key={s.title} className="flex flex-col">
                <h3 className="mb-3 font-display text-xl font-medium text-foreground">{s.title}</h3>
                <p className="flex-1 leading-relaxed text-muted-foreground">{s.body}</p>
                {s.links && (
                  <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                    {s.links.map((l) => (
                      <ArrowLink key={l.label} to={l.to}>
                        {l.label}
                      </ArrowLink>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </Section>

        {/* Om Tiego */}
        <Section tone="muted">
          <img
            src={officePhoto}
            alt={t(
              "Tiegos kontor med Tiego-logotypen på väggen och medarbetare vid skrivborden",
              "Tiego's office with the Tiego logo on the wall and employees at their desks",
            )}
            width={1600}
            height={710}
            loading="lazy"
            decoding="async"
            className="mb-14 aspect-[16/7] w-full rounded-2xl object-cover shadow-card"
          />
          <div className="grid gap-12 lg:grid-cols-[2fr_3fr] lg:gap-16">
            <dl className="grid grid-cols-3 gap-6 self-start lg:grid-cols-1">
              {[
                { value: "2012", label: t("Grundat", "Founded") },
                { value: "20", label: t("Medarbetare", "Employees") },
                { value: "3", label: t("Kontor: Stockholm, Luleå och Norrköping", "Offices: Stockholm, Luleå and Norrköping") },
              ].map((s) => (
                <div key={s.label} className="flex flex-col-reverse border-l-2 border-gold pl-4">
                  <dt className="text-sm text-muted-foreground">{s.label}</dt>
                  <dd className="font-display text-3xl text-foreground md:text-4xl">{s.value}</dd>
                </div>
              ))}
            </dl>
            <div>
              <SectionHeading eyebrow={t("Om Tiego", "About Tiego")} title={t("Specialister sedan 2012", "Specialists since 2012")} />
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                {t(
                  "Tiego grundades 2012 med en tanke: att erbjuda kompetenta ekonomer krävande uppdrag och samtidigt bygga system som förändrar vardagen för koncernekonomer. I dag är vi 20 medarbetare med kontor i Stockholm, Luleå och Norrköping.",
                  "Tiego was founded in 2012 with one idea: to offer skilled accountants demanding assignments while building systems that change everyday life for group accountants. Today we are 20 employees with offices in Stockholm, Luleå and Norrköping.",
                )}
              </p>
              <div className="mt-8">
                <ArrowLink to={localePath("/karriar")}>{t("Lär känna oss", "Get to know us")}</ArrowLink>
              </div>
            </div>
          </div>
        </Section>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
